import { useEffect, useState } from 'react';
import {
  Building2,
  Users,
  Mail,
  Crown,
  Calendar,
  Hash,
  Package,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Activity,
} from 'lucide-react';
import { membersApi } from '../../../api/client';
import type { OrgSummary } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  accent?: 'brand' | 'win' | 'warn' | 'loss' | 'default';
}

function StatCard({ icon: Icon, label, value, sub, accent = 'default' }: StatCardProps) {
  const ACCENT: Record<string, string> = {
    brand:   'text-brand bg-brand/10 border-brand/20',
    win:     'text-win bg-win/10 border-win/20',
    warn:    'text-warn bg-warn/10 border-warn/20',
    loss:    'text-loss bg-loss/10 border-loss/20',
    default: 'text-gray-400 bg-surface-3 border-surface-4',
  };
  return (
    <div className="op-panel p-4 flex items-start gap-4">
      <div className={`p-2 rounded-lg border flex-shrink-0 ${ACCENT[accent]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</p>
        <p className="text-xl font-semibold text-gray-100 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-0.5 truncate">{sub}</p>}
      </div>
    </div>
  );
}

export function AdminOverviewPage() {
  const { can } = usePermissions();
  const canView = can('organization.view');

  const [summary, setSummary] = useState<OrgSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return;
    setIsLoading(true);
    membersApi
      .getSummary()
      .then(setSummary)
      .catch((err) => setError((err as Error).message ?? 'Failed to load organization data'))
      .finally(() => setIsLoading(false));
  }, [canView]);

  if (!canView) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <p className="text-sm text-gray-500">You don't have permission to view organization details.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Organization Overview</h2>
        <p className="text-sm text-gray-500 mt-1">
          Summary of your organization's operational state and key metrics.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading organization data…
        </div>
      ) : summary ? (
        <>
          {/* Stats grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard
              icon={Users}
              label="Active Members"
              value={summary.memberCount}
              accent="brand"
            />
            <StatCard
              icon={Mail}
              label="Pending Invitations"
              value={summary.pendingInviteCount}
              accent={summary.pendingInviteCount > 0 ? 'warn' : 'default'}
            />
            <StatCard
              icon={Package}
              label="Subscription Tier"
              value={summary.subscriptionTier.charAt(0).toUpperCase() + summary.subscriptionTier.slice(1)}
              accent="default"
            />
          </div>

          {/* Details card */}
          <div className="op-panel p-4 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-brand" />
              <h3 className="text-sm font-semibold text-gray-200">Organization Details</h3>
            </div>
            <dl className="space-y-0">
              {[
                { icon: Building2, label: 'Name',          value: summary.name },
                { icon: Hash,      label: 'Organization ID', value: summary.id },
                { icon: Activity,  label: 'Slug',           value: summary.slug },
                { icon: Calendar,  label: 'Created',        value: formatDate(summary.createdAt) },
                { icon: Crown,     label: 'Owner',          value: summary.ownerDisplayName ?? summary.ownerEmail ?? '—' },
                { icon: Package,   label: 'Plan',           value: summary.subscriptionTier.charAt(0).toUpperCase() + summary.subscriptionTier.slice(1) },
              ].map(({ icon: RowIcon, label, value }) => (
                <div
                  key={label}
                  className="flex items-center justify-between py-2.5 border-b border-surface-4 last:border-0 gap-4"
                >
                  <dt className="flex items-center gap-2 text-sm text-gray-400 flex-shrink-0">
                    <RowIcon className="w-3.5 h-3.5 text-gray-600" />
                    {label}
                  </dt>
                  <dd className="text-sm text-gray-200 text-right truncate max-w-[60%] font-mono text-xs">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Status card */}
          <div className="op-panel p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-4 h-4 text-win flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-200">Organization is active</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  All membership and invitation workflows are operational.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
